import { useEffect, useMemo, useRef, useState } from 'react'
import { Autoplay } from 'swiper/modules'
import {
  Swiper,
  SwiperClass,
  SwiperRef,
  SwiperSlide,
  useSwiperSlide,
} from 'swiper/react'
import { PostType } from '~/widgets/Stories'
import { CloseIcon } from '~/shared/ui/icons'
import { myPalette } from '~/shared/ui/theme'
import { Post } from './Post'
import { ProgressBar } from './ProgressBar'
import { CloseButton } from './styled'

import 'swiper/css'

type StoryProps = {
  posts: PostType[]
  onAllPostsEnd: () => void
  onClose: () => void
}

const DEFAULT_INDEX = 0

export function Posts({ posts, onAllPostsEnd, onClose }: StoryProps) {
  const { isActive } = useSwiperSlide()

  const swiperRef = useRef<SwiperRef>(null)
  const [currentIndex, setCurrentIndex] = useState<number>(DEFAULT_INDEX)
  const lastStoriesIndex = useMemo(() => posts?.length - 1, [posts?.length])

  const handleSlideChange = () => {
    const activeIndex = swiperRef?.current?.swiper.activeIndex ?? DEFAULT_INDEX
    setCurrentIndex(activeIndex)
  }

  const handleStop = () => {
    swiperRef?.current?.swiper?.autoplay?.stop?.()
  }
  const handleStart = () => {
    swiperRef?.current?.swiper?.autoplay?.start?.()
  }
  const handlePause = () => {
    swiperRef?.current?.swiper?.autoplay?.pause?.()
  }
  const handleResume = () => {
    swiperRef?.current?.swiper?.autoplay?.resume?.()
  }

  const handleNext = () => {
    if (lastStoriesIndex === currentIndex) {
      onAllPostsEnd()
      return
    }
    swiperRef?.current?.swiper.slideNext(100)
  }

  const handleBack = () => {
    swiperRef?.current?.swiper.slidePrev(100)
    handleStart()
  }

  const handleProgressBar = (progress: number) => {
    const percent = `${(1 - progress) * 100}%`
    const currentBar = progressRefs.current[currentIndex]
    if (currentBar) {
      currentBar.style.setProperty('--progress', percent)
    }
  }

  const progressRefs = useRef<HTMLDivElement[]>([])

  const onAutoplayTimeLeft = (
    swiper: SwiperClass,
    time: number,
    progress: number,
  ) => {
    handleProgressBar(progress)

    if (time <= 0 && currentIndex === lastStoriesIndex) {
      swiper.autoplay.stop()
      onAllPostsEnd()
    }
  }

  useEffect(() => {
    if (isActive) {
      handleStart()
    } else {
      handleResume()
      handleStop()
    }
  }, [isActive])

  return (
    <>
      <ProgressBar
        count={posts.length}
        current={currentIndex}
        progressRefs={progressRefs}
      />

      <CloseButton onClick={onClose} data-testid='story-modal-close-button'>
        <CloseIcon />
      </CloseButton>

      <Swiper
        ref={swiperRef}
        onSlideChange={handleSlideChange}
        onBeforeInit={handleStop}
        onDestroy={handleStop}
        autoplay={{
          delay: 10000,
          stopOnLastSlide: true,
        }}
        modules={[Autoplay]}
        onAutoplayTimeLeft={onAutoplayTimeLeft}
        style={{
          height: '100%',
          borderRadius: '4px',
          background: myPalette.black.main,
        }}
        allowTouchMove={false}
      >
        {posts.map((post, index) => {
          const shouldRender = Math.abs(currentIndex - index) <= 1
          return (
            <SwiperSlide key={post.id}>
              {shouldRender && (
                <Post
                  onNext={handleNext}
                  onBack={handleBack}
                  onPause={handlePause}
                  onResume={handleResume}
                  post={post}
                />
              )}
            </SwiperSlide>
          )
        })}
      </Swiper>
    </>
  )
}
